import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useYear } from "@/context/YearContext";
import { formatINR, formatFullINR, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, ShieldCheck, PiggyBank, Rocket, Receipt, Lock, Undo2, ArrowRightLeft } from "lucide-react";

const FUND_LABELS = {
  operating: "Operating Reserve", next_year: "Next-Year Reserve", founder: "Founder Family Savings",
  innovation: "Innovation & Growth", tax: "Taxes & Statutory Reserve",
};
const TYPE_LABELS = {
  snapshot: "Verified Balance Snapshot", capital_added: "Capital Added", capital_withdrawn: "Capital Withdrawn",
  adjustment_increase: "Adjustment Increase", adjustment_decrease: "Adjustment Decrease",
  transfer: "Transfer Between Funds", tax_payment: "Tax Payment", reversal: "Reversal",
};
const FUND_META = [
  { key: "operating_reserve", fund: "operating", label: "Current-Year Operating Reserve", icon: Landmark, color: "#3B82F6", desc: "Current year's working capital. Goes down each month as recurring expenses are paid.", protectedFund: false, testId: "fund-card-operating" },
  { key: "next_year_saved", fund: "next_year", label: "Next-Year Reserve (NYMR)", icon: ShieldCheck, color: "#10B981", desc: "You fund this every month for next year. Cannot be used for day-to-day expenses.", protectedFund: true, testId: "fund-card-next-year" },
  { key: "founder_savings", fund: "founder", label: "Founder Family Savings", icon: PiggyBank, color: "#F59E0B", desc: "30% for the founder's family, taken after NYMR (Next Year's Monthly Reserve) is paid.", protectedFund: true, testId: "fund-card-founder-savings" },
  { key: "innovation_growth_balance", fund: "innovation", label: "Innovation & Growth Fund", icon: Rocket, color: "#8B5CF6", desc: "Whatever remains each month after NYMR and the founder family's share. Only fund for growth spending.", protectedFund: false, testId: "fund-card-innovation-growth" },
  { key: "tax_reserve", fund: "tax", label: "Taxes & Statutory Reserve", icon: Receipt, color: "#F43F5E", desc: "Money kept aside for GST, income tax and other dues. Goes down only when tax is paid.", protectedFund: true, testId: "fund-card-tax" },
];

export default function Funds() {
  const { year } = useYear();
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [t, setT] = useState({ type: "capital_added", fund: "operating", to_fund: "next_year", amount: "", effective_date: "", bank_account: "", reference: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [d, l] = await Promise.all([
      api.get("/dashboard", { params: { year } }),
      api.get("/ledger", { params: { year } }),
    ]);
    setData(d.data);
    setEntries(l.data);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const amt = parseFloat(t.amount);
    if (t.amount === "" || isNaN(amt) || amt < 0) { toast.error("Enter a valid non-negative amount"); return; }
    setSaving(true);
    try {
      await api.post("/ledger", {
        year, fund: t.type === "tax_payment" ? "tax" : t.fund, type: t.type, amount: amt,
        to_fund: t.type === "transfer" ? t.to_fund : null,
        effective_date: t.effective_date || null, bank_account: t.bank_account,
        reference: t.reference, notes: t.notes,
      });
      toast.success(`${TYPE_LABELS[t.type]} recorded`);
      setT({ ...t, amount: "", bank_account: "", reference: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transaction failed");
    } finally { setSaving(false); }
  };

  const reverse = async (e) => {
    const reason = window.prompt(`Reverse this ${TYPE_LABELS[e.type]} of ${formatINR(e.amount)}?\nA written reason is mandatory:`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    try {
      await api.post(`/ledger/${e.id}/reverse`, { reason });
      toast.success("Entry reversed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reversal failed"); }
  };

  if (!data) return <div className="text-zinc-500 py-20 text-center">Loading…</div>;

  const isTransfer = t.type === "transfer";
  const isSnapshot = t.type === "snapshot";

  return (
    <div data-testid="funds-view-container" className="space-y-8">
      <div className="fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">Fund Balances & Ledger</h1>
        <p className="text-sm text-zinc-500 mt-1">{year} · Five separate balances. Every money movement goes through the ledger — nothing is overwritten or deleted.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {FUND_META.map((fm, idx) => (
          <div key={fm.key} data-testid={`funds-page-${fm.testId}`} className="card-surface p-6 fade-up border-l-2" style={{ animationDelay: `${idx * 50}ms`, borderLeftColor: fm.color }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <fm.icon size={17} style={{ color: fm.color }} />
                <p className="font-heading font-semibold text-zinc-900 text-sm">{fm.label}</p>
              </div>
              {fm.protectedFund && (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-2 py-0.5">
                  <Lock size={9} /> Protected
                </span>
              )}
            </div>
            <p className="font-mono-num text-2xl font-bold text-zinc-900">{formatINR(data[fm.key])}</p>
            <p className="text-xs text-zinc-400 font-mono-num mt-0.5">{formatFullINR(data[fm.key])}</p>
            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">{fm.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card-surface p-6 fade-up space-y-4 lg:col-span-2 h-fit" data-testid="ledger-form">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">New Ledger Transaction</p>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Transaction Type</label>
            <Select value={t.type} onValueChange={(v) => setT({ ...t, type: v })}>
              <SelectTrigger data-testid="ledger-type-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).filter(([k]) => k !== "reversal").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">{isTransfer ? "From Fund" : "Fund"}</label>
              <Select value={t.type === "tax_payment" ? "tax" : t.fund} onValueChange={(v) => setT({ ...t, fund: v })} disabled={t.type === "tax_payment"}>
                <SelectTrigger data-testid="ledger-fund-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FUND_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isTransfer && (
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">To Fund</label>
                <Select value={t.to_fund} onValueChange={(v) => setT({ ...t, to_fund: v })}>
                  <SelectTrigger data-testid="ledger-tofund-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FUND_LABELS).filter(([k]) => k !== t.fund).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isTransfer ? "col-span-2" : ""}>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">{isSnapshot ? "Verified Balance (₹)" : "Amount (₹)"}</label>
              <Input data-testid="ledger-amount-input" type="number" min="0" placeholder="0"
                className={`bg-[#F8F7F3] border-[#E3E0D8] font-mono-num ${parseFloat(t.amount) < 0 ? "border-red-400" : ""}`}
                value={t.amount} onChange={(e) => setT({ ...t, amount: e.target.value })} />
              {t.amount !== "" && parseFloat(t.amount) >= 0 && <p className="text-xs text-blue-600 mt-1 font-mono-num">{formatINR(parseFloat(t.amount))} · {formatFullINR(parseFloat(t.amount))}</p>}
              {parseFloat(t.amount) < 0 && <p className="text-[11px] text-red-600 mt-1">Negative values are not allowed</p>}
            </div>
          </div>
          {isSnapshot && (
            <p className="text-[11px] text-zinc-500 bg-[#F8F7F3] border border-[#ECE9E1] rounded-lg px-3 py-2">
              A snapshot creates a <span className="font-semibold">reconciliation adjustment</span> between the verified bank balance and the computed balance. History is never overwritten.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Effective Date</label>
              <Input data-testid="ledger-date-input" type="date" className="bg-[#F8F7F3] border-[#E3E0D8]"
                value={t.effective_date} onChange={(e) => setT({ ...t, effective_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Bank / Account</label>
              <Input data-testid="ledger-bank-input" placeholder="e.g. HDFC Current" className="bg-[#F8F7F3] border-[#E3E0D8]"
                value={t.bank_account} onChange={(e) => setT({ ...t, bank_account: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Transaction Reference</label>
            <Input data-testid="ledger-reference-input" placeholder="UTR / cheque no." className="bg-[#F8F7F3] border-[#E3E0D8]"
              value={t.reference} onChange={(e) => setT({ ...t, reference: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Notes</label>
            <Textarea data-testid="ledger-notes-input" rows={2} className="bg-[#F8F7F3] border-[#E3E0D8]"
              value={t.notes} onChange={(e) => setT({ ...t, notes: e.target.value })} />
          </div>
          <Button data-testid="ledger-save-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold" onClick={submit} disabled={saving}>
            {isTransfer && <ArrowRightLeft size={14} className="mr-2" />}
            {saving ? "Recording…" : "Record Transaction"}
          </Button>
        </div>

        <div className="card-surface fade-up overflow-hidden lg:col-span-3" data-testid="ledger-table">
          <div className="px-6 pt-6 pb-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Transaction Ledger · {year}</p>
          </div>
          <div className="overflow-x-auto max-h-[640px]">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="px-6 py-2.5 font-medium">Date</th>
                  <th className="px-3 py-2.5 font-medium">Fund</th>
                  <th className="px-3 py-2.5 font-medium">Type</th>
                  <th className="px-3 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-3 py-2.5 font-medium">Ref / Bank</th>
                  <th className="px-3 py-2.5 font-medium">By</th>
                  <th className="px-3 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">No ledger transactions for {year}</td></tr>}
                {entries.map((e) => (
                  <tr key={e.id} className={`border-b border-zinc-100 last:border-0 ${e.reversed_by ? "opacity-45" : ""}`}>
                    <td className="px-6 py-2.5 text-xs text-zinc-500">{e.effective_date}<p className="text-[10px] text-zinc-400">{formatDateTime(e.created_at)}</p></td>
                    <td className="px-3 py-2.5 text-xs font-medium text-zinc-700">{FUND_LABELS[e.fund]}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] uppercase tracking-wide text-zinc-500 border border-[#E3E0D8] bg-[#FCFBF8] rounded-full px-2 py-0.5">{TYPE_LABELS[e.type]}</span>
                      {e.reversed_by && <span className="ml-1 text-[10px] uppercase text-red-600">reversed</span>}
                      {e.notes && <p className="text-[11px] text-zinc-400 mt-0.5 max-w-[180px] truncate" title={e.notes}>{e.notes}</p>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono-num font-semibold ${e.direction === "increase" ? "text-emerald-600" : "text-red-600"}`}>
                      {e.direction === "increase" ? "+" : "−"}{formatINR(e.amount)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-zinc-500">{e.reference || "—"}{e.bank_account ? ` · ${e.bank_account}` : ""}</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-500">{e.entered_by}</td>
                    <td className="px-3 py-2.5">
                      {e.type !== "reversal" && !e.reversed_by && (
                        <button data-testid={`ledger-reverse-${e.id}`} onClick={() => reverse(e)}
                          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-red-600" title="Reverse (reason required)">
                          <Undo2 size={12} /> Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
